import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from '../../api/axios';
import { toast } from 'react-toastify';

const roleColors = {
  super_admin: '#f1870e',
  system_admin: '#ff9800',
  tca: '#2e7d32',
  invigilator: '#e65100',
  attender: '#6a1b9a'
};

const UserList = () => {

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/users');
      setUsers(res.data || []);
    } catch (err) {
      console.log(err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this user?');
    if (!confirmDelete) return;

    try {
      await axios.delete(`/users/${id}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const handleToggle = async (userData) => {
    try {
      await axios.put(`/users/${userData.id}`, {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        department: userData.department,
        role_id: userData.role_id,
        is_active: userData.is_active ? 0 : 1
      });

      toast.success(
        `User ${userData.is_active ? 'deactivated' : 'activated'} successfully`
      );

      fetchUsers();

    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();

    return (
      (u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)) &&
      (roleFilter ? u.role_name === roleFilter : true)
    );
  });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        color: '#666',
        fontSize: '18px'
      }}>
        Loading users...
      </div>
    );
  }

  return (
    <div style={{
      padding: '32px',
      backgroundColor: '#f5f6fa',
      minHeight: '100vh'
    }}>

      {/* HEADER */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>

        <div>
          <h2 style={{
            color: '#f1870e',
            margin: 0,
            fontSize: '26px',
            fontWeight: 700
          }}>
            Users
          </h2>

          <p style={{
            color: '#888',
            margin: '4px 0 0',
            fontSize: '14px'
          }}>
            {users.length} total users
          </p>
        </div>

        <button
          onClick={() => navigate('/users/add')}
          style={{
            backgroundColor: '#f1870e',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px'
          }}
        >
          + Add User
        </button>

      </div>

      {/* SEARCH & FILTER */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>

        <input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: 1,
            minWidth: '220px',
            padding: '10px 14px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px'
          }}
        />

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{
            padding: '10px 14px',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: 'white'
          }}
        >
          <option value="">All Roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="system_admin">System Admin</option>
          <option value="tca">TCA</option>
          <option value="invigilator">Invigilator</option>
          <option value="attender">Attender</option>
        </select>

      </div>

      {/* TABLE */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>

        {filtered.length === 0 ? (
          <div style={{
            textAlign: 'center',
            color: '#aaa',
            padding: '60px 0',
            fontSize: '16px'
          }}>
            No users found.
          </div>
        ) : (
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>

            <thead>
              <tr style={{ backgroundColor: '#f1870e' }}>
                {['#','Name','Email','Phone','Department','Role','Status','Actions'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '13px 14px',
                      textAlign: 'left',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'uppercase'
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>

              {filtered.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0' }}>

                  <td style={{ padding: '13px 14px', color: '#aaa' }}>{i + 1}</td>

                  <td style={{ padding: '13px 14px', fontWeight: 600 }}>{u.name}</td>

                  <td style={{ padding: '13px 14px', color: '#555' }}>{u.email}</td>

                  <td style={{ padding: '13px 14px', color: '#555' }}>{u.phone || '—'}</td>

                  <td style={{ padding: '13px 14px', color: '#555' }}>{u.department || '—'}</td>

                  <td style={{ padding: '13px 14px' }}>
                    <span style={{
                      backgroundColor: roleColors[u.role_name] || '#666',
                      color: 'white',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      {u.role_name?.replace(/_/g, ' ')}
                    </span>
                  </td>

                  <td style={{ padding: '13px 14px' }}>
                    <span style={{
                      backgroundColor: u.is_active ? '#e8f5e9' : '#ffebee',
                      color: u.is_active ? '#2e7d32' : '#c62828',
                      padding: '3px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td style={{ padding: '13px 14px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>

                      <button
                        onClick={() => navigate(`/users/edit/${u.id}`)}
                        style={{
                          backgroundColor: '#fff3e0',
                          color: '#f1870e',
                          border: 'none',
                          padding: '5px 11px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600
                        }}
                      >
                        Edit
                      </button>

                      <button
                        onClick={() => handleToggle(u)}
                        style={{
                          backgroundColor: '#e8f5e9',
                          color: '#2e7d32',
                          border: 'none',
                          padding: '5px 11px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600
                        }}
                      >
                        Toggle
                      </button>

                      {currentUser?.role === 'super_admin' && (
                        <button
                          onClick={() => handleDelete(u.id)}
                          style={{
                            backgroundColor: '#ffebee',
                            color: '#c62828',
                            border: 'none',
                            padding: '5px 11px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 600
                          }}
                        >
                          Delete
                        </button>
                      )}

                    </div>
                  </td>

                </tr>
              ))}

            </tbody>

          </table>
        )}

      </div>

    </div>
  );
};

export default UserList;